import React from "react";
import { useState } from "react";
import {
  ArrowRight,
  Check,
  FileSearch,
  Link2,
  Search,
  ShieldCheck,
  Sparkles,
} from "./icons";
import type { IconProps } from "./icons";
import type { ComponentType } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Select,
  SelectItem,
  cx,
} from "./ui";

const MATCHES = [
  { line: "0042", source: "Precision valve assy, stainless", part: "PV-8842", maker: "Hanse Industrial", origin: "DE", product: "Precision Valve Assembly", sku: "PV-8842-DE", confidence: 96, signals: ["SKU", "Description", "Manufacturer", "Prior entries"], issue: "Declared value variance" },
  { line: "0118", source: "Controller module X7 / 24V", part: "X7-CTRL", maker: "Shenzhen Apex", origin: "CN", product: "X7 Control Module", sku: "X7-CTRL-24", confidence: 92, signals: ["Part number", "Description", "Manufacturer"], issue: "Section 301 exposure" },
  { line: "0207", source: "Cotton knit mens pullover", part: "MKP-410", maker: "Dhaka Knitworks", origin: "BD", product: "Men's Knit Pullover", sku: "MKP-410-NV", confidence: 78, signals: ["Description", "Origin", "Value pattern"], issue: "Manufacturer evidence missing" },
];

export const AceCatalogMatch: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [confirmed, setConfirmed] = useState<string[]>(["0042"]);

  return (
    <>
      <section className="ih-card">
        <div className="ih-row-wrap ih-between ih-gap-4" style={{ alignItems: "flex-start" }}>
          <div className="ih-row-top ih-gap-3" style={{ maxWidth: 720 }}>
            <div className="ih-section-icon"><FileSearch size={20} /></div>
            <div>
              <div className="ih-row-wrap ih-gap-2">
                <h2 className="ih-font-semibold">ACE Analyzer → Product Catalog</h2>
                <span className="ih-badge ih-badge-info">Preview</span>
              </div>
              <p className="ih-text-xs ih-text-muted ih-mt-1">
                Connect ES-003 findings to the right catalog product—even when the HTS code is missing or unreliable. BITE combines product language, part numbers, manufacturers, origin, value patterns, and prior entry history.
              </p>
            </div>
          </div>
          <Button variant="primary" size="sm" onClick={() => setOpen(true)}>
            <Sparkles size={14} /> Review catalog matches
          </Button>
        </div>
        <div className="ih-ace-flow ih-mt-4">
          <FlowStep icon={FileSearch} label="ES-003 report" detail="Line-level findings" />
          <ArrowRight size={16} className="ih-text-muted" />
          <FlowStep icon={Link2} label="HTS-independent match" detail="Multi-signal confidence" emphasized />
          <ArrowRight size={16} className="ih-text-muted" />
          <FlowStep icon={ShieldCheck} label="Product 360" detail="Issues linked to history" />
        </div>
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent size="xl" onClose={() => setOpen(false)}>
          <DialogHeader>
            <DialogTitle>
              <span className="ih-row ih-gap-2">
                Catalog Match Review <span className="ih-badge ih-badge-info">Preview</span>
              </span>
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className="ih-callout-info">
              <Sparkles size={20} className="ih-tone-info" />
              <div>
                <p className="ih-font-semibold">Reliable matching without a reliable HTS code</p>
                <p className="ih-text-xs ih-text-muted ih-mt-1">
                  Traditional joins fail when ES-003 tariff data is incomplete. BITE proposes a catalog identity from the surrounding evidence, then keeps a human in control.
                </p>
              </div>
            </div>
            <div className="ih-row ih-between ih-text-xs ih-mt-3">
              <span className="ih-text-muted">ACE_ES003_June_2026.txt · 3 proposed matches</span>
              <span className="ih-font-medium">1 confirmed · 2 to review</span>
            </div>
            <div className="ih-col ih-gap-3 ih-mt-3">
              {MATCHES.map((match) => {
                const isConfirmed = confirmed.includes(match.line);
                return (
                  <article
                    key={match.line}
                    className={cx("ih-subsurface", "ih-ace-match", isConfirmed && "is-confirmed")}
                  >
                    <div className="ih-ace-match-grid">
                      <div>
                        <div className="ih-kicker">ES-003 line {match.line}</div>
                        <div className="ih-font-medium ih-mt-1">{match.source}</div>
                        <div className="ih-mono ih-text-xs ih-text-muted ih-mt-1">
                          {match.part} · {match.maker} · {match.origin}
                        </div>
                      </div>
                      <div className="ih-ace-confidence">
                        <div className="ih-num" style={{ fontSize: 18, fontWeight: 600 }}>
                          {match.confidence}%
                        </div>
                        <ArrowRight size={14} />
                        <span className="ih-text-xs">confidence</span>
                      </div>
                      <div>
                        <div className="ih-kicker">Proposed catalog product</div>
                        <Select defaultValue={match.sku} className="ih-mt-1">
                          <SelectItem value={match.sku}>
                            {match.product} · {match.sku}
                          </SelectItem>
                          <SelectItem value="search">Search another product…</SelectItem>
                          <SelectItem value="new">Create new catalog product</SelectItem>
                        </Select>
                      </div>
                    </div>
                    <div className="ih-ace-match-foot">
                      <div className="ih-row-wrap ih-gap-1">
                        <span className="ih-text-xs ih-text-muted">Matched on</span>
                        {match.signals.map((s) => (
                          <span key={s} className="ih-badge ih-badge-success">{s}</span>
                        ))}
                        <span className="ih-badge ih-badge-warning">Issue: {match.issue}</span>
                      </div>
                      <div className="ih-row ih-gap-2">
                        <Button variant="outline" size="sm">
                          <Search size={14} /> Correct
                        </Button>
                        <Button
                          variant={isConfirmed ? "outline" : "primary"}
                          size="sm"
                          onClick={() =>
                            setConfirmed((old) =>
                              old.includes(match.line)
                                ? old.filter((l) => l !== match.line)
                                : [...old, match.line],
                            )
                          }
                        >
                          <Check size={14} /> {isConfirmed ? "Confirmed" : "Confirm match"}
                        </Button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </DialogBody>
          <DialogFooter>
            <div className="ih-row ih-gap-2 ih-grow">
              <Link2 size={16} className="ih-tone-info" />
              <p className="ih-text-xs">
                <strong>After confirmation:</strong> ACE issues attach to each product record, Product 360 timeline, and entry-package history.
              </p>
            </div>
            <Button variant="primary" disabled={confirmed.length < MATCHES.length}>
              Apply {confirmed.length} matches
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

const FlowStep: React.FC<{
  icon: ComponentType<IconProps>;
  label: string;
  detail: string;
  emphasized?: boolean;
}> = ({
  icon: Icon,
  label,
  detail,
  emphasized,
}) => {
  return (
    <div className={cx("ih-row ih-gap-2", emphasized && "ih-tone-info")}>
      <Icon size={16} />
      <div>
        <div className="ih-font-semibold">{label}</div>
        <div className="ih-text-xs ih-text-muted">{detail}</div>
      </div>
    </div>
  );
}
